import React, { useMemo, useState } from 'react'
import {
    Box,
    Card,
    CardContent,
    Typography,
    IconButton,
    CircularProgress
} from '@material-ui/core'
import { useStyles } from './styles'
import { useSelector } from 'react-redux'
import { CheckCircle, CircleXIcon, PencilIcon, RotateCcw } from 'lucide-react'
import moment from 'moment'
import { useQuery } from 'react-query'
import { getDeviceThresholdBaitActivities } from '../../api/device-api'
import {
    getBaitActivityThresholdDataFromString,
    getDeviceShortStatusText
} from 'web/src/utils'
import { useDispatch } from 'react-redux'
import { enqueueSnackbar } from 'web/src/app/root/actions'
import { DeviceConfirmThresholdResetModal } from '../device-confirm-threshold-reset-modal'
import { REPORT_TYPES } from 'web/src/app/root/hooks/useReportTypes'

export const DeviceBaitActivitiesCard = ({
    device,
    onConfirmThresholdReset,
    onSetCurrentTab,
    overridingLocationSettings
}) => {
    const classes = useStyles()
    const { theme, locale } = useSelector(state => state.main)
    const dispatch = useDispatch()
    const usesLocationBaitSettings =
        Boolean(overridingLocationSettings?.data?.bait_settings) &&
        !device.override_location_settings
    const baitSettings = usesLocationBaitSettings
        ? overridingLocationSettings.data.bait_settings
        : device
    const [
        isConfirmThresholdResetModalOpen,
        setIsConfirmThresholdResetModalOpen
    ] = useState(false)
    const {
        data: thresholdBaitActivities,
        isLoading: isLoadingThresholdBaitActivities
    } = useQuery(
        ['getDeviceThresholdBaitActivities', device.id],
        () => getDeviceThresholdBaitActivities(device.id),
        {
            refetchOnWindowFocus: false,
            refetchInterval: false,
            enabled: true,
            onError: () => {
                dispatch(
                    enqueueSnackbar({
                        message: locale.sagasMsgs.commonLoadingError,
                        options: {
                            variant: 'error'
                        }
                    })
                )
            }
        }
    )
    const { threshold, unitsAmount, unit } = useMemo(() => {
        return getBaitActivityThresholdDataFromString(
            baitSettings.activity_alert_threshold
        )
    }, [baitSettings.activity_alert_threshold])

    const isThresholdReached = useMemo(() => {
        return [
            REPORT_TYPES.activity_threshold_reached,
            REPORT_TYPES.activity_threshold_reached_and_bait_low
        ].includes(device.last_report_type)
    }, [device.last_report_type])

    return (
        <Box>
            <Typography>{locale.devices.activitiesThreshold}</Typography>
            <Card className={classes.deviceInfoCard}>
                <CardContent className={classes.deviceInfoCardContent}>
                    <Box className={classes.deviceInfoCardHeader}>
                        <div>
                            {usesLocationBaitSettings ? (
                                <div
                                    style={{
                                        marginBottom: 10,
                                        color: theme.textSecondary
                                    }}>
                                    {`ⓘ ${locale.locations.lBasedSettingsAreApplied}`}
                                </div>
                            ) : null}
                            <Box className={classes.activityIconWrapper}>
                                {isThresholdReached ? (
                                    <CircleXIcon
                                        size={
                                            usesLocationBaitSettings ? 24 : 32
                                        }
                                        color={theme.error}
                                    />
                                ) : (
                                    <CheckCircle
                                        size={
                                            usesLocationBaitSettings ? 24 : 32
                                        }
                                        color={theme.active}
                                    />
                                )}
                            </Box>
                        </div>
                        {usesLocationBaitSettings ? null : (
                            <IconButton
                                onClick={() => onSetCurrentTab('settings')}>
                                <PencilIcon
                                    size={20}
                                    color={theme.textPrimary}
                                />
                            </IconButton>
                        )}
                    </Box>

                    <Box
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}>
                        <Box className={classes.activityCountWrapper}>
                            {isThresholdReached ? (
                                <>
                                    <div style={{ fontSize: 16 }}>
                                        {getDeviceShortStatusText(
                                            device,
                                            locale
                                        )}
                                    </div>
                                    {device?.data?.bait?.l_o_a_t_r_at ? (
                                        <div style={{ fontSize: 16 }}>
                                            (
                                            {moment(
                                                device?.data?.bait?.l_o_a_t_r_at
                                            ).format('DD.MM.YYYY HH:mm')}
                                            )
                                        </div>
                                    ) : null}
                                </>
                            ) : (
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'baseline',
                                        gap: 8
                                    }}>
                                    <Typography
                                        className={classes.activityCount}>
                                        <>
                                            {thresholdBaitActivities ? (
                                                thresholdBaitActivities.length
                                            ) : (
                                                <CircularProgress
                                                    size={20}
                                                    color={theme.primary}
                                                />
                                            )}
                                            /{threshold}
                                        </>
                                    </Typography>
                                    <Typography
                                        className={classes.activityPeriod}>
                                        {locale.devices.last} {unitsAmount}{' '}
                                        {unit === 'h'
                                            ? locale.devices.hours
                                            : locale.devices.days}
                                    </Typography>
                                </div>
                            )}
                        </Box>
                        <IconButton
                            onClick={() =>
                                setIsConfirmThresholdResetModalOpen(true)
                            }>
                            <RotateCcw size={20} color={theme.textPrimary} />
                        </IconButton>
                    </Box>

                    <Box className={classes.activityResetWrapper}>
                        <Typography className={classes.activityResetLabel}>
                            {locale.devices.lastThresholdReset ||
                                locale.devices.lastThresholdReset}
                            :{' '}
                            {device.last_activity_reset_at
                                ? moment(device.last_activity_reset_at).format(
                                      'DD.MM.YYYY HH:mm'
                                  )
                                : '--'}{' '}
                            {device.last_activity_reset_by_name
                                ? `(${device.last_activity_reset_by_name})`
                                : ''}
                        </Typography>
                    </Box>
                </CardContent>
            </Card>
            <DeviceConfirmThresholdResetModal
                open={isConfirmThresholdResetModalOpen}
                setOpen={setIsConfirmThresholdResetModalOpen}
                onConfirmClick={onConfirmThresholdReset}
                deviceId={device.id}
            />
        </Box>
    )
}
